<template>
  <div>
    <base-page-heading title="Imports">
      <template #extra>
        <b-button
          variant="primary"
          class="ml-2"
          @click="() => $router.push({ name: 'imports-new' })"
        >
          <i class="fa fa-plus mr-1" />
          New Import
        </b-button>
      </template>
    </base-page-heading>
    <div class="content">
      <b-row>
        <b-col>
          <imports-list-filter
            :columns="columns"
            :initial-filters="appliedFilters"
            @filters-applied="applyFilters"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-table
            v-if="isLoaded"
            id="imports-table"
            :items="records"
            :fields="columns"
            :busy="isLoadingData"
            show-empty
            responsive
            table
          >
            <template #cell(user_id)="data">
              <b-link
                v-if="data.item.user_id"
                :to="{ name: 'user', params: { id: data.item.user.id } }"
              >
                {{ data.item.user.name }}
              </b-link>
            </template>
            <template #cell(category)="data">
              {{ data.item.category || "depreciated_import" | humanizedFormat | capitalizeFormat }}
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusBadge(data.item.status)">
                {{ data.item.status | humanizedFormat | capitalizeFormat }}
              </b-badge>
            </template>
            <template #cell(file)="data">
              <b-link :href="data.item.file.url">
                Download
              </b-link>
            </template>
            <template #cell(error_file)="data">
              <b-link
                v-if="data.item.error_file"
                :href="data.item.error_file.url"
              >
                Download
              </b-link>
            </template>
            <template #cell(completed_at)="data">
              <span v-if="data.item.completed_at">{{ data.item.completed_at | dateTimeFormat }}</span>
            </template>
            <template #cell(created_at)="data">
              <div v-if="data.item.created_at">
                <div>{{ data.item.created_at | dateTimeFormat }}</div>
                <div
                  v-if="importDuration(data.item).text"
                  class="font-size-sm"
                  :class="importDuration(data.item).isRunning ? 'text-info font-w600' : 'text-muted'"
                >
                  {{ importDuration(data.item).text }}
                </div>
              </div>
            </template>
            <template #cell(num_imported)="data">
              {{ data.item.num_imported | numberWithDelimeter }}
            </template>
            <template #cell(num_errors)="data">
              {{ data.item.num_errors | numberWithDelimeter }}
            </template>
          </b-table>
          <sfs-skeleton-table
            v-else
            :rows="pagination.perPage"
            :columns="columns.length"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="auto"
          :class="{ 'mr-auto': true, 'ml-auto': !showPerPage }"
        >
          <b-pagination
            v-if="showPagination"
            v-model="pagination.currentPage"
            :total-rows="totalRows"
            :per-page="pagination.perPage"
            last-class="d-none"
          />
        </b-col>
        <sfs-pagination-dropdown
          container-class="col-auto"
          :current-page="pagination.currentPage"
          :per-page="pagination.perPage"
          :total-rows="totalRows"
          @per-page-changed="onPerPageChanged"
        />
      </b-row>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import ImportsListFilter from '@/components/admin/imports/ListFilter.vue';
import urlMixin from '@/mixins/urlMixin';
import tableMixin from '@/mixins/tableMixin';
import { formatDateTimeDifference } from '@/utils/formatDateTimeDifference';
import { IMPORT_TABLE_DEFAULT_COLUMNS, SUCCEEDED_IMPORT_STATUS, RUNNING_IMPORT_STATUS, FAILED_IMPORT_STATUS } from '@/constants/imports';

export default {
  name: 'ImportsList',
  components: {
    ImportsListFilter,
  },
  mixins: [urlMixin, tableMixin],
  props: {
    user_id: String,
    status: String,
    date_from: String,
    date_to: String,
    category: String,
    columns: {
      type: Array,
      default: () => IMPORT_TABLE_DEFAULT_COLUMNS,
    },
  },
  data() {
    return {
      appliedFilters: {
        user_id: this.user_id,
        status: this.status,
        date_from: this.date_from,
        date_to: this.date_to,
        category: this.category,
      },
      loaded: false,
      totalRows: 0,
      now: DateTime.now(),
      nowTimer: null,
    };
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      if (this.records.some((record) => record.status === RUNNING_IMPORT_STATUS)) {
        this.now = DateTime.now();
      }
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.nowTimer);
  },
  methods: {
    importDuration(item) {
      if (!item.started_at) {
        return { text: '', isRunning: false };
      }

      const isRunning = item.status === RUNNING_IMPORT_STATUS || !item.completed_at;
      const end = isRunning ? this.now : item.completed_at;
      const durationText = formatDateTimeDifference(item.started_at, end, { alwaysShowSeconds: isRunning });

      if (!durationText) {
        return { text: '', isRunning: false };
      }

      return {
        text: isRunning ? `Running for ${durationText}` : `Took ${durationText}`,
        isRunning,
      };
    },
    getRecords() {
      const params = {
        page: this.pagination.currentPage,
        per_page: this.pagination.perPage,
        ...this.appliedFilters,
      };
      this.isLoadingData = true;
      this.$importsAPI
        .getImports(params)
        .then((response) => {
          const { total, imports } = response;
          this.totalRows = total;
          this.records = imports;
        })
        .catch(() => {
          this.records = [];
        })
        .finally(() => {
          this.loaded = true;
          this.isLoadingData = false;
        });
    },
    statusBadge(status) {
      switch (status) {
        case SUCCEEDED_IMPORT_STATUS:
          return 'success';
        case RUNNING_IMPORT_STATUS:
          return 'info';
        case FAILED_IMPORT_STATUS:
          return 'warning';
        default:
          return 'secondary';
      }
    },
    applyFilters({ filters }) {
      this.appliedFilters = Object.assign({}, filters);
      this.appendObjectToQueryParams(this.appliedFilters);
      this.getRecords();
    },
  },
};
</script>
